import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { createWorkspace } from "../../data/schema";
import { showToast } from "components/ui-kit/Toast";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (workspaceId: string) => void;
};

export const CreateWorkspaceModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const max = 100;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const remaining = useMemo(() => `${name.length}/${max}`, [name, max]);
  if (!open) return null;

  const handleCreate = async () => {
    if (isSubmitting) return;

    const candidateName = name.slice(0, max);
    const hasName = candidateName.trim().length > 0;

    setIsSubmitting(true);
    try {
      const ws = await createWorkspace(candidateName);
      if (!hasName) {
        showToast("Workspace created with default name", undefined, "success");
      }
    onCreated?.(ws.id);
    onClose();
    setName("");
    } catch (error) {
      console.error("Failed to create workspace", error);
      showToast("Unable to create workspace. Please try again.", undefined, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Create New Workspace</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        <SubTitle>Enter a name for your new workspace</SubTitle>
        <InputRow>
          <NameInput
            placeholder="Untitled Workspace"
            value={name}
            maxLength={max}
            onChange={(e) => setName(e.target.value)}
          />
          <CharCounter>{remaining}</CharCounter>
        </InputRow>
        <Footer>
          <PrimaryButton onClick={handleCreate} disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Workspace"}
          </PrimaryButton>
        </Footer>
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }
  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 44vw;
  max-width: calc(100% - 6vw);
  background: #fff;
  color: #000;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 2.2vh 2vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1vh;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  width: 3.4vh;
  height: 3.4vh;
  border-radius: 1.7vh;
  border: none;
  background: #f4f4f4;
  color: #333;
  font-size: 20px;
  cursor: pointer;
`;

const SubTitle = styled.div`
  color: #555;
  margin-bottom: 1.4vh;
`;

const InputRow = styled.div`
  position: relative;
  margin-bottom: 2vh;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 1.4vh 1.2vw;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  outline: none;
  font-size: 14px;
`;

const CharCounter = styled.div`
  position: absolute;
  right: 1.2vw;
  bottom: 1vh;
  color: #999;
  font-size: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  padding: 1.2vh 1.4vw;
  border-radius: 18px;
  border: none;
  background: #7987FF;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: #6b7cff; }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #7987FF;
  }
`;

export default CreateWorkspaceModal;



